import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import API from '../api';
import { FiArrowLeft, FiLock, FiAlertTriangle, FiCopy, FiCheckCircle, FiXCircle, FiClock } from 'react-icons/fi';

const CardsPage = ({ user, setUser, formatDate, setPath }) => {
  const getCreateMinAmount = (type) => (type === 'mp' ? 0 : type === 'sr' ? 5 : 10);
  const getCreateMaxAmount = (type) => (type === 'mp' ? 0 : undefined);
  const getTopupMinAmount = (card) => (card?.service_id === '3' ? 5 : 1);
  const getTopupFeeRate = (card) => (
    card?.service_id === '3' ? 0.02 : card?.service_id === '2' ? 0.025 : 0.03
  );
  const formatCardPan = (pan) => {
    if (!pan) return '';
    const digitsOnly = String(pan).replace(/\D/g, '');
    if (!digitsOnly) return String(pan).trim();
    return digitsOnly.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  const [cards, setCards] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(() => {
    const stored = localStorage.getItem('selectedCardId');
    return stored ? parseInt(stored, 10) : null;
  });
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupInput, setTopupInput] = useState('');
  const [topupInputError, setTopupInputError] = useState('');
  const [isToppingUp, setIsToppingUp] = useState(false);
  const [cardSearch, setCardSearch] = useState('');
  const [cardTypeDropdownOpen, setCardTypeDropdownOpen] = useState(false);
  const [showCreateCardModal, setShowCreateCardModal] = useState(false);
  const [newCardName, setNewCardName] = useState('');
  const [newCardLimit, setNewCardLimit] = useState('');
  const [isCreatingCard, setIsCreatingCard] = useState(false);
  const [newCardType, setNewCardType] = useState('mp');
  const [newCardLimitError, setNewCardLimitError] = useState('');
  const [hasCompletedVerification, setHasCompletedVerification] = useState(false);
  const [showVisaIssueModal, setShowVisaIssueModal] = useState(false);
  // State to track whether the user is currently choosing the card type.
  // We start with "false" every time the component mounts so that simply navigating
  // back to the "Cartes" menu always shows the list view instead of the card-type
  // selector that might have been open during a previous visit.
  const [choosingCardType, setChoosingCardType] = useState(false);

  useEffect(() => {
    if (!showVisaIssueModal) return undefined;

    const scrollY = window.scrollY;
    const previousBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShowVisaIssueModal(false);
    };
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      Object.assign(document.body.style, previousBodyStyles);
      window.scrollTo(0, scrollY);
    };
  }, [showVisaIssueModal]);

  const showCreateCardFlow = useCallback(() => {
    setNewCardName('');
    setNewCardLimit('');
    setSelectedCardId(null);
    setPreviewData(null);
    setPreviewError(null);
    localStorage.removeItem('selectedCardId');
    setChoosingCardType(true);
  }, []);

  const redirectToVerification = useCallback(() => {
    const verificationPath = '/verification?returnTo=/cards&action=create-card';
    window.history.pushState(null, '', verificationPath);
    setPath('/verification');
  }, [setPath]);

  const openCreateCardModal = useCallback(async () => {
    if (hasCompletedVerification) {
      showCreateCardFlow();
      return;
    }

    if (!cards || cards.length === 0) {
      try {
        const res = await API.get('/verification/');
        if (res.data?.verified) {
          setHasCompletedVerification(true);
        } else {
          redirectToVerification();
          return;
        }
      } catch (err) {
        console.error(err);
        redirectToVerification();
        return;
      }
    }
    showCreateCardFlow();
  }, [cards, hasCompletedVerification, redirectToVerification, showCreateCardFlow]);

  // If navigation to the Cards page was triggered from the Dashboard "Nouvelle carte"
  // button, we store a flag in sessionStorage so that the first render of this page
  // can immediately open the create-card flow. We read & clear it here.
  useEffect(() => {
    const shouldStart = sessionStorage.getItem('startCreateCard');
    if (shouldStart === 'true') {
      sessionStorage.removeItem('startCreateCard');
      openCreateCardModal();
    }
  }, [openCreateCardModal]);

  // Fetch cards when component mounts
  useEffect(() => {
    API.get('/cards/')
      .then((res) => setCards(res.data))
      .catch((err) => {
        console.error(err);
        setCards([]);
      });
  }, []);

  // Fetch card preview when a persisted selection exists or changes
  useEffect(() => {
    if (selectedCardId) {
      setPreviewData(null);
      setPreviewError(null);
      API.get(`/cards/${selectedCardId}/preview/`)
        .then((res) => setPreviewData(res.data))
        .catch((err) => {
          console.error(err);
          setPreviewError('Failed to load card details');
        });
    }
  }, [selectedCardId]);

  // Poll cards with pending creation every 5 seconds
  useEffect(() => {
    if (!cards) return;
    const hasPending = cards.some(c => c.creation_status === 'PENDING');
    if (!hasPending) return;
    const intervalId = setInterval(() => {
      API.get('/cards/')
        .then(res => setCards(res.data))
        .catch(() => {/* ignore */});
    }, 5000);
    return () => clearInterval(intervalId);
  }, [cards]);

  // Close card-type dropdown when clicking outside
  useEffect(() => {
    if (!cardTypeDropdownOpen) return;
    const handleOutside = (e) => {
      if (!e.target.closest('.card-type-dropdown')) setCardTypeDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [cardTypeDropdownOpen]);

  // The card-type selector should NOT persist across menu navigations, so we no
  // longer store it in sessionStorage. Removing this effect prevents the
  // component from restoring the previous "choosing" state when the user simply
  // re-opens the "Cartes" section.

  const handleCardClick = (cardId) => {
    setSelectedCardId(cardId);
    localStorage.setItem('selectedCardId', cardId);
    setPreviewData(null);
    setPreviewError(null);
  };

  const copyToClipboard = async (text, label) => {
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(`${label} copié`);
    } catch (e) {
      toast.error(`Impossible de copier ${label}`);
    }
  };

  const openTopupModal = () => {
    setTopupInput('');
    setTopupInputError('');
    setShowTopupModal(true);
  };

  const handleTopup = async () => {
    if (!topupInput) {
      setTopupInputError('Veuillez entrer un montant');
      return;
    }
    const amt = parseFloat(topupInput);
    if (isNaN(amt) || amt <= 0) {
      setTopupInputError('Montant invalide');
      return;
    }

    // Check if selected card exists
    if (!selectedCard) {
      toast.error("Carte sélectionnée introuvable.");
      setShowTopupModal(false);
      setTopupInput('');
      setIsToppingUp(false);
      return;
    }
    const minTopupAmount = getTopupMinAmount(selectedCard);
    if (amt < minTopupAmount) {
      setTopupInputError(`Montant minimum: ${minTopupAmount} USD requis`);
      return;
    }
    setTopupInputError('');
    setIsToppingUp(true);

    // Calculate fee and total
    const fee = amt * getTopupFeeRate(selectedCard);
    const totalAmount = amt + fee;

    // Check balance
    if (!user || user.usd_balance < totalAmount) {
      toast.error(`Solde insuffisant. Requis : ${totalAmount.toFixed(2)} USD`);
      setShowTopupModal(false);
      setTopupInput('');
      setIsToppingUp(false);
      return;
    }

    const idempotencyKey = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    try {
      await API.post(
        `/cards/${selectedCardId}/topup/`,
        { amount: amt, idempotency_key: idempotencyKey }
      );
      toast.success('Top up in process');
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error('Failed to request top up');
      }
    } finally {
      setTimeout(() => {
        setIsToppingUp(false);
        setShowTopupModal(false);
        setTopupInput('');
        setTopupInputError('');
      }, 500);
    }
  };

  const applyNewCardTypeDefaults = (type) => {
    setNewCardType(type);
    setNewCardLimit(type === 'mp' ? '0' : '');
    setNewCardLimitError('');
  };

  const handleCreateCard = async () => {
    if (!newCardName || !newCardLimit) {
      toast.error('Veuillez remplir le nom et la limite');
      return;
    }
    const limitVal = parseFloat(newCardLimit);
    const minLimit = getCreateMinAmount(newCardType);
    const maxLimit = getCreateMaxAmount(newCardType);
    if (isNaN(limitVal) || limitVal < minLimit || (maxLimit !== undefined && limitVal > maxLimit)) {
      const amountRule = maxLimit === minLimit
        ? `Le montant d'émission doit être de ${minLimit} USD`
        : `Minimum ${minLimit} USD`;
      toast.error(amountRule);
      setNewCardLimitError(amountRule);
      return;
    }
    setIsCreatingCard(true);
    const idempotencyKey = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    try {
      const endpoint = newCardType === 'wr'
        ? '/cards/create-wr/'
        : (newCardType === 'sr' ? '/cards/create-sr/' : '/cards/create-us/');
      const payload = {
        card_name: newCardName,
        [newCardType === 'wr' ? 'all_time_internet_purchase' : 'initial_amount']: limitVal,
        idempotency_key: idempotencyKey
      };
      const res = await API.post(endpoint, payload);
      const createdCard = res.data;
      setCards(prev => prev ? [createdCard, ...prev] : [createdCard]);
      setShowCreateCardModal(false);
      setNewCardName('');
      setNewCardLimit('');
      setNewCardType('mp');
      setNewCardLimitError('');
      toast.success(`Carte ${newCardType === 'wr' ? 'Visa' : 'Mastercard'} en cours de création`);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.code === 'verification_required') {
        redirectToVerification();
      } else if (err.response?.data?.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error("Échec de la création de carte");
      }
    } finally {
      setIsCreatingCard(false);
    }
  };

  const selectedCard = selectedCardId && cards ? cards.find((c) => c.id === selectedCardId) : null;
  const isWrCard = selectedCard?.service_id === '1';
  const selectedCardTopupMin = getTopupMinAmount(selectedCard);
  const selectedCardTopupFeeRate = getTopupFeeRate(selectedCard);
  const topupAmount = parseFloat(topupInput);
  const hasValidTopupAmount = topupInput !== '' && !Number.isNaN(topupAmount) && topupAmount >= selectedCardTopupMin;
  const topupFeeAmount = hasValidTopupAmount ? topupAmount * selectedCardTopupFeeRate : 0;
  const topupTotalAmount = hasValidTopupAmount ? topupAmount + topupFeeAmount : 0;

  return (
    <>
      {showVisaIssueModal && (
        <div
          className="modal-overlay visa-issue-modal-overlay"
          role="presentation"
          onClick={() => setShowVisaIssueModal(false)}
        >
          <div
            className="modal visa-issue-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="visa-issue-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="visa-issue-modal-header">
              <h2 id="visa-issue-modal-title">Olana amin&apos;ny Visa Publicité</h2>
              <button
                type="button"
                className="visa-issue-modal-close"
                aria-label="Hidio"
                onClick={() => setShowVisaIssueModal(false)}
              >
                ×
              </button>
            </div>
            <div className="visa-issue-modal-content">
              <p>
                Misy olana ara-teknika eo amin&apos;ny partenaire-nay mpamokatra ny carte Visa
                Publicité amin&apos;izao fotoana izao. Miandry ny valiny avy amin&apos;izy ireo izahay
                ary manantena hahafantatra ny anton&apos;ilay olana farafahatarany amin&apos;ny Talata izao.
              </p>
              <p>
                Ho vahaolana vonjimaika dia afaka mamorona carte hafa vaovao ianao afahanao manao ny
                paiement maika. Misy cartes roa hafa azo isafidianana sy azo ampiasaina amin&apos;ny
                Boost Facebook sy publicité hafa dia ny : Mastercard Publicité sy Mastercard Universelle
                amin&apos;ny vidiny latsaka 50% (2 USD au lieu de 4 USD).
              </p>
              <p>
                Raha mbola bloqué hatramin&apos;ny Talata ny carte teo aloha dia afaka mangataka ny
                remboursement ny frais de création an&apos;ilay carte vaovao ianao.
              </p>
              <p>
                Ho an&apos;ny vola tavela ao amin&apos;ny carte teo aloha dia mifandraisa amin&apos;ny
                service client mba hamindrana izany any amin&apos;ny compte USD anao.
              </p>
              <p>
                Vonona hanampy anao haingana izahay isan&apos;andro, amin&apos;ny 8 ora maraina ka
                hatramin&apos;ny 10 ora alina.
              </p>
              <div className="visa-issue-contact">
                <div>Tél: <a href="tel:+261380954168">038 09 541 68</a></div>
                <div>
                  Page Adabo Cash:{' '}
                  <a href="https://www.facebook.com/adabocash" target="_blank" rel="noopener noreferrer">
                    https://www.facebook.com/adabocash
                  </a>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="primary-btn visa-issue-modal-action"
              onClick={() => setShowVisaIssueModal(false)}
            >
              <FiCheckCircle aria-hidden="true" /> OK
            </button>
          </div>
        </div>
      )}
      {showTopupModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Recharger la carte</h2>
            <div className="modal-section">
              <form className="modal-form" onSubmit={(e) => { e.preventDefault(); handleTopup(); }}>
                <input
                  type="number"
                  value={topupInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    const amount = parseFloat(value);
                    setTopupInput(value);
                    if (value && !Number.isNaN(amount) && amount < selectedCardTopupMin) {
                      setTopupInputError(`Montant minimum: ${selectedCardTopupMin} USD requis`);
                    } else {
                      setTopupInputError('');
                    }
                  }}
                  placeholder="Entrer le montant"
                  step="1"
                />
                {topupInputError && (
                  <div className="topup-input-error">{topupInputError}</div>
                )}
                <div className="topup-fee-info">
                  {hasValidTopupAmount && (
                    <span className="topup-fee-total">
                      Total débité: {topupTotalAmount.toFixed(2)} USD
                    </span>
                  )}
                </div>
                <div className="modal-button-container">
                  <button type="submit" className={`primary-btn modal-button-flex ${isToppingUp ? 'modal-button-loading' : ''}`} disabled={isToppingUp}>{isToppingUp ? 'Rechargement...' : 'Recharger'}</button>
                  <button type="button" className="close-btn modal-button-flex modal-button-no-margin-top" onClick={() => setShowTopupModal(false)}>Annuler</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showCreateCardModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Créer une carte</h2>
            <div className="modal-section">
              <form className="modal-form" onSubmit={(e) => { e.preventDefault(); handleCreateCard(); }}>
                <div className="card-type-form-group">
                  <label className="card-type-label">Type de carte</label>
                  <div className="custom-dropdown card-type-dropdown">
                    <div
                      className={`dropdown-selected card-type-dropdown-selected${cardTypeDropdownOpen ? ' open' : ''}`}
                      onClick={() => setCardTypeDropdownOpen(!cardTypeDropdownOpen)}
                    >
                      {newCardType === 'wr' ? (
                        <span className="card-type-dropdown-option">
                          <img src="https://upload.wikimedia.org/wikipedia/commons/5/5c/Visa_Inc._logo_%282021%E2%80%93present%29.svg" alt="Visa" className="payment-logo dropdown" />
                          Visa publicité
                        </span>
                      ) : newCardType === 'sr' ? (
                        <span className="card-type-dropdown-option">
                          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Mastercard_2019_logo.svg/langfr-1280px-Mastercard_2019_logo.svg.png" alt="Mastercard" className="payment-logo dropdown" />
                          Mastercard universelle
                        </span>
                      ) : (
                        <span className="card-type-dropdown-option">
                          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Mastercard_2019_logo.svg/langfr-1280px-Mastercard_2019_logo.svg.png" alt="Mastercard" className="payment-logo dropdown" />
                          Mastercard Publicité
                        </span>
                      )}
                    </div>
                    <div className={`dropdown-options card-type-dropdown-options${cardTypeDropdownOpen ? ' open' : ''}`}>
                      <div
                        className="dropdown-option card-type-dropdown-option is-unavailable"
                        aria-disabled="true"
                        title="Création indisponible pour le moment"
                      >
                        <img src="https://upload.wikimedia.org/wikipedia/commons/5/5c/Visa_Inc._logo_%282021%E2%80%93present%29.svg" alt="Visa" className="payment-logo dropdown" />
                        Visa publicité
                        <span className="card-type-unavailable-label">Indisponible</span>
                      </div>
                      <div
                        className={`dropdown-option card-type-dropdown-option${newCardType === 'sr' ? ' selected' : ''}`}
                        onClick={() => {
                          applyNewCardTypeDefaults('sr');
                          setCardTypeDropdownOpen(false);
                        }}
                      >
                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Mastercard_2019_logo.svg/langfr-1280px-Mastercard_2019_logo.svg.png" alt="Mastercard" className="payment-logo dropdown" />
                        Mastercard universelle
                      </div>
                      <div
                        className={`dropdown-option card-type-dropdown-option${newCardType === 'mp' ? ' selected' : ''}`}
                        onClick={() => {
                          applyNewCardTypeDefaults('mp');
                          setCardTypeDropdownOpen(false);
                        }}
                      >
                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Mastercard_2019_logo.svg/langfr-1280px-Mastercard_2019_logo.svg.png" alt="Mastercard" className="payment-logo dropdown" />
                        Mastercard Publicité
                      </div>
                    </div>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Nom de la carte"
                  value={newCardName}
                  onChange={e => setNewCardName(e.target.value)}
                  required
                />
                <input
                  type="number"
                  placeholder={newCardType === 'mp' ? 'Dépôt initial (USD) : 0' : `Dépot initial (USD, min ${getCreateMinAmount(newCardType)})`}
                  value={newCardLimit}
                  readOnly={newCardType === 'mp'}
                  onChange={e => {
                    const val = e.target.value;
                    setNewCardLimit(val);
                    const numVal = parseFloat(val);
                    const minLimit = getCreateMinAmount(newCardType);
                    if (val && !isNaN(numVal) && numVal < minLimit) {
                      setNewCardLimitError(`Minimum ${minLimit} USD requis`);
                    } else {
                      setNewCardLimitError('');
                    }
                  }}
                  min={getCreateMinAmount(newCardType)}
                  max={getCreateMaxAmount(newCardType)}
                  step="1"
                  required
                />
                {newCardLimitError && <div className="card-limit-error">{newCardLimitError}</div>}
                <div className="modal-button-container">
                  <button type="submit" className="primary-btn modal-button-flex" disabled={isCreatingCard || !!newCardLimitError}>{isCreatingCard ? 'Création...' : 'Créer'}</button>
                  <button type="button" className="close-btn modal-button-flex modal-button-no-margin-top" onClick={() => setShowCreateCardModal(false)} disabled={isCreatingCard}>Annuler</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <section className={`cards-section ${choosingCardType ? 'choosing-card-type' : ''}`}>
        {selectedCardId ? (
          <>
            <button className="back-to-cards-btn" onClick={() => { setSelectedCardId(null); setPreviewData(null); localStorage.removeItem('selectedCardId'); }}>
              <span className="back-to-cards-content">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="back-arrow-icon"><path d="M12.5 15L7.5 10L12.5 5" stroke="#0a87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Retour aux cartes
              </span>
            </button>
            <div className="card-preview-container">
              <div className="card-preview-section">
                {selectedCard?.service_id === '1' && (
                  <div className="visa-issue-banner" role="alert">
                    <button
                      type="button"
                      className="visa-issue-banner-trigger"
                      onClick={() => setShowVisaIssueModal(true)}
                      aria-haspopup="dialog"
                    >
                      <FiAlertTriangle className="visa-issue-banner-icon" />
                      <span className="visa-issue-banner-copy">
                        <strong>Olana amin&apos;ny Visa Publicité</strong>
                        <span>
                          Misy olana ara-teknika eo amin&apos;ny partenaire-nay. Afaka mampiasa Mastercard
                          Publicité na Mastercard Universelle ianao.
                        </span>
                      </span>
                      <span className="visa-issue-more-btn">
                        Voir plus
                      </span>
                    </button>
                  </div>
                )}
                {previewError ? (
                  <div className="error-message">{previewError}</div>
                ) : (
                  <div className="card-preview-section-layout">
                    <div className="card-preview-main-content">
                      {/* Card basic information */}
                      <div className="card-info">
                        <h3 className="card-info-title">
                          {selectedCard ? selectedCard.card_name : <span className="skeleton skeleton-text skeleton-width-140" />}
                        </h3>
                        <p className="card-balance card-info-balance">
                          {(() => {
                            if (!previewData || previewData.balance === undefined) {
                              return <span className="skeleton skeleton-text skeleton-width-80" />;
                            }
                            const val = parseFloat(previewData.balance);
                            if (Number.isNaN(val)) {
                              return <span className="error-text">Erreur</span>;
                            }
                            return `${val.toFixed(2)} ${previewData.currency || ''}`;
                          })()}
                        </p>
                        {/* Status moved below */}
                        <div className="card-label card-info-label">Adresse de facturation</div>
                        {selectedCard ? (
                          selectedCard.service_id === '1' ? (
                            <address className="card-info-address">
                              <span className="copyable-row">
                                <span>Estonia, Tallinn, F. R. Kreutzwaldi 4</span>
                                <button
                                  type="button"
                                  className="copyable-btn"
                                  title="Copier l'adresse"
                                  onClick={() => copyToClipboard('Estonia, Tallinn, F. R. Kreutzwaldi 4', 'Adresse')}
                                >
                                  <FiCopy className="copy-hint-icon" />
                                </button>
                              </span>
                              <br />
                              <span className="copyable-row">
                                <span>Code postal 10120</span>
                                <button
                                  type="button"
                                  className="copyable-btn"
                                  title="Copier le code postal"
                                  onClick={() => copyToClipboard('10120', 'Code postal')}
                                >
                                  <FiCopy className="copy-hint-icon" />
                                </button>
                              </span>
                            </address>
                          ) : selectedCard.service_id === '3' ? (
                            <address className="card-info-address">
                              <span className="copyable-row">
                                <span>USA, Dallas, TX, 120 Turtle Creek Boulevard, 505</span>
                                <button
                                  type="button"
                                  className="copyable-btn"
                                  title="Copier l'adresse"
                                  onClick={() => copyToClipboard('USA, Dallas, TX, 120 Turtle Creek Boulevard, 505', 'Adresse')}
                                >
                                  <FiCopy className="copy-hint-icon" />
                                </button>
                              </span>
                              <br />
                              <span className="copyable-row">
                                <span>Code postal 75207</span>
                                <button
                                  type="button"
                                  className="copyable-btn"
                                  title="Copier le code postal"
                                  onClick={() => copyToClipboard('75207', 'Code postal')}
                                >
                                  <FiCopy className="copy-hint-icon" />
                                </button>
                              </span>
                            </address>
                          ) : (
                            <address className="card-info-address">
                              <span className="copyable-row">
                                <span>36‑38 Westbourne Grove, Newton Road, London</span>
                                <button
                                  type="button"
                                  className="copyable-btn"
                                  title="Copier l'adresse"
                                  onClick={() => copyToClipboard('36-38 Westbourne Grove, Newton Road, London', 'Adresse')}
                                >
                                  <FiCopy className="copy-hint-icon" />
                                </button>
                              </span>
                              <br />
                              <span className="copyable-row">
                                <span>Code postal W2 5SH</span>
                                <button
                                  type="button"
                                  className="copyable-btn"
                                  title="Copier le code postal"
                                  onClick={() => copyToClipboard('W2 5SH', 'Code postal')}
                                >
                                  <FiCopy className="copy-hint-icon" />
                                </button>
                              </span>
                            </address>
                          )
                        ) : (
                          <>
                            <span className="skeleton skeleton-text skeleton-block" />
                            <span className="skeleton skeleton-text skeleton-block-small" />
                          </>
                        )}
                        {/* Status indicator removed (kept on card visual only) */}
                        {/* Decline warning */}
                        <div className="card-warning">
                          <FiAlertTriangle className="warning-icon" /> Un taux élevé de paiements refusés peut entraîner le blocage de votre carte.
                        </div>
                        <button
                          onClick={openTopupModal}
                          type="button"
                          className="topup-btn"
                          disabled={isWrCard}
                          title={isWrCard ? 'Rechargement indisponible pour cette carte' : undefined}
                        >
                          Recharger
                        </button>
                      </div>

                      {/* Card visualisation */}
                      {(() => {
                        const statusRaw = previewData && previewData.status;
                        const isBlockedCard = statusRaw && !(String(statusRaw).toLowerCase() === 'active' || statusRaw === '1');
                        return (
                          <div className={`card-visualization ${isBlockedCard ? 'card-blocked' : ''}`}> 
                                {(() => {
                                  const raw = previewData?.status;
                                  const lower = String(raw ?? '').toLowerCase();
                                  const isActive = raw === '1' || lower === 'active';
                                  const isBlocked = raw === '0' || lower === 'blocked' || lower === 'bloquée';
                                  const label = isBlocked ? 'Bloquée' : (isActive ? 'Active' : '');
                                  return label ? (
                                    <div className={`card-status-chip ${isBlocked ? 'blocked' : 'active'}`}>
                                      {isBlocked && <FiLock className="lock-icon-inverse" />} {label}
                                    </div>
                                  ) : null;
                                })()}
                            {/* Card Brand Logo at top right, replacing card_type text */}
                            {selectedCard && (selectedCard.service_id === '1' || selectedCard.service_id === '2' || selectedCard.service_id === '3') && (
                              <img
                                src={selectedCard.service_id === '1'
                                  ? 'https://upload.wikimedia.org/wikipedia/commons/5/5c/Visa_Inc._logo_%282021%E2%80%93present%29.svg'
                                  : 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Mastercard_2019_logo.svg/langfr-1280px-Mastercard_2019_logo.svg.png'}
                                alt={selectedCard.service_id === '1' ? 'Visa' : 'Mastercard'}
                                className={`card-brand-logo-absolute ${selectedCard.service_id === '1' ? 'card-brand-logo-visa-height' : 'card-brand-logo-mastercard-height'}`}
                              />
                            )}
                            <div className="card-number card-number-centered">
                              <button
                                type="button"
                                className="copyable-btn copyable-inverse"
                                title="Cliquer pour copier"
                                onClick={() => previewData?.pan && copyToClipboard(formatCardPan(previewData.pan).replace(/\s+/g, ''), 'Numéro de carte')}
                              >
                              {previewData && previewData.pan
                                ? formatCardPan(previewData.pan)
                                : `**** **** **** ${selectedCard ? selectedCard.card_four_digits : '****'}`}
                                <FiCopy className="copy-hint-icon" />
                              </button>
                            </div>
                            <div className="card-details card-details-bottom">
                              <button
                                type="button"
                                className="copyable-btn copyable-inverse"
                                title="Cliquer pour copier"
                                onClick={() => previewData?.expDate && copyToClipboard(previewData.expDate, "Date d'expiration")}
                              >
                                EXP {previewData?.expDate ?? <span className="skeleton skeleton-text skeleton-width-40" />}
                                <FiCopy className="copy-hint-icon" />
                              </button>
                              <button
                                type="button"
                                className="copyable-btn copyable-inverse"
                                title="Cliquer pour copier"
                                onClick={() => previewData?.cvv && copyToClipboard(String(previewData.cvv), 'CVV')}
                              >
                                CVV {previewData?.cvv ?? <span className="skeleton skeleton-text skeleton-width-30" />}
                                <FiCopy className="copy-hint-icon" />
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Transactions */}
                    <div className="transactions-full-width">
                      <div className="cards-table-wrapper transactions-table-overflow">
                        <table className="cards-table is-transactions">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Marchand</th>
                              <th>Numéro</th>
                              <th>Montant</th>
                              <th>Frais</th>
                              <th>Devise</th>
                              <th>Statut</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData === null ? (
                              <tr>
                                <td colSpan="6" className="table-cell-center">
                                  Chargement des transactions...
                                </td>
                              </tr>
                            ) : previewData.transactions.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="table-cell-center">
                                  Aucune transaction trouvée.
                                </td>
                              </tr>
                            ) : (
                              previewData.transactions.map((tx, index) => (
                                <tr key={index}>
                                  <td>
                                    {new Date(tx.date).toLocaleDateString()}<br />
                                    {new Date(tx.date).toLocaleTimeString()}
                                  </td>
                                  <td>{tx.merchant}</td>
                                  <td>{selectedCard ? `*${selectedCard.card_four_digits}` : '****'}</td>
                                  <td>{parseFloat(tx.amount).toFixed(2)}</td>
                                  <td>
                                    {(() => {
                                      if (tx.fee !== undefined && tx.fee !== null && tx.fee !== '') {
                                        const parsedFee = parseFloat(tx.fee);
                                        return Number.isNaN(parsedFee) ? tx.fee : parsedFee.toFixed(2);
                                      }
                                      if (!selectedCard) return '';
                                      if (selectedCard.service_id === '2') return '0.25';
                                      if (selectedCard.service_id === '3') {
                                        const lower = String(tx.status ?? '').toLowerCase();
                                        const isFailed = lower.includes('declin') || lower.includes('refus');
                                        return isFailed ? '0.50' : '0.00';
                                      }
                                      return '0.0';
                                    })()}
                                  </td>
                                  <td>{tx.currency}</td>
                                  <td>
                                    {(() => {
                                      const raw = tx.status;
                                      const lower = String(raw ?? '').toLowerCase();
                                      const isApproved = lower === 'approved' || raw === '1';
                                      const isDeclined = lower === 'declined' || raw === '0';
                                      const isPending = lower === 'pending' || lower === 'processing';
                                      const label = isApproved ? 'Approuvée' : isDeclined ? 'Refusée' : isPending ? 'En cours' : raw;
                                      return (
                                        <span className={`tx-status-badge ${isApproved ? 'is-approved' : isDeclined ? 'is-declined' : isPending ? 'is-pending' : ''}`}>
                                          {isApproved && <FiCheckCircle className="tx-status-icon" />}
                                          {isDeclined && <FiXCircle className="tx-status-icon" />}
                                          {isPending && <FiClock className="tx-status-icon" />}
                                          {label}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : choosingCardType ? (
          <>
            <div className="card-type-container">
              <div className="card-type-header">
                <h2>Choisir le type de carte</h2>
              </div>
              <div className="card-type-grid">
                <article
                  className="card-type-card is-mastercard"
                  role="button"
                  tabIndex={0}
                  onClick={() => { applyNewCardTypeDefaults('sr'); setChoosingCardType(false); setShowCreateCardModal(true); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyNewCardTypeDefaults('sr'); setChoosingCardType(false); setShowCreateCardModal(true); } }}
                >
                  <header className="card-type-card-header">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Mastercard_2019_logo.svg/langfr-1280px-Mastercard_2019_logo.svg.png" alt="Mastercard" className="card-type-card-brand" />
                    <h3>Mastercard universelle</h3>
                    <p className="card-type-services-text">Boost Facebook, Alibaba, Taobao, eBay, Amazon et plus</p>
                    <div className="card-type-badges">
                      <span className="card-type-badge">Toutes devises</span>
                      <span className="card-type-badge">Création : 2 USD (Promo)</span>
                      <span className="card-type-badge">Frais de transaction : 0.25 USD</span>
                      <span className="card-type-badge">Frais de rechargement : 2.5% (Promo)</span>
                </div>
                  </header>
                  <ul className="card-type-features">
                    <li>Premier dépôt : 5 USD</li>
                    <li>Recharge minimum : 1 USD</li>
                    <li className="card-type-restriction"><strong>Restrictions :</strong> Interdit sur Starlink et services crypto</li>
                  </ul>
                  <footer className="card-type-card-footer">
                  <button
                    type="button"
                      className="select-card-btn"
                      onClick={(e) => { e.stopPropagation(); applyNewCardTypeDefaults('sr'); setChoosingCardType(false); setShowCreateCardModal(true); }}
                  >
                    Choisir cette carte
                  </button>
                  </footer>
                </article>
                <article
                  className="card-type-card is-mastercard"
                  role="button"
                  tabIndex={0}
                  onClick={() => { applyNewCardTypeDefaults('mp'); setChoosingCardType(false); setShowCreateCardModal(true); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); applyNewCardTypeDefaults('mp'); setChoosingCardType(false); setShowCreateCardModal(true); } }}
                >
                  <header className="card-type-card-header">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Mastercard_2019_logo.svg/langfr-1280px-Mastercard_2019_logo.svg.png" alt="Mastercard" className="card-type-card-brand" />
                    <h3>Mastercard Publicité</h3>
                    <p className="card-type-services-text">Boost Facebook, Google Ads, IA</p>
                    <div className="card-type-badges">
                      <span className="card-type-badge">USD</span>
                      <span className="card-type-badge">Création : 2 USD (Promo)</span>
                      <span className="card-type-badge">Paiements US en USD : 0%</span>
                      <span className="card-type-badge">Paiements transfrontaliers : 4%</span>
                      <span className="card-type-badge">Paiements non-USD : 2%</span>
                      <span className="card-type-badge">Transaction refusée : 0.30 USD</span>
                      <span className="card-type-badge">Frais de rechargement : 2% (Promo)</span>
                    </div>
                  </header>
                  <ul className="card-type-features">
                    <li>Premier dépôt : 0 USD</li>
                    <li>Recharge minimum : 5 USD</li>
                  </ul>
                  <footer className="card-type-card-footer">
                    <button
                      type="button"
                      className="select-card-btn"
                      onClick={(e) => { e.stopPropagation(); applyNewCardTypeDefaults('mp'); setChoosingCardType(false); setShowCreateCardModal(true); }}
                    >
                      Choisir cette carte
                    </button>
                  </footer>
                </article>
                <article
                  className="card-type-card is-visa is-unavailable"
                  title="Création indisponible pour le moment"
                >
                  <header className="card-type-card-header">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/5/5c/Visa_Inc._logo_%282021%E2%80%93present%29.svg" alt="Visa" className="card-type-card-brand" />
                    <h3>Visa publicité</h3>
                    <p className="card-type-services-text">Boost Facebook, Google Ads, VPN, domaines, pubs, abonnements, et encore plus</p>
                    <div className="card-type-badges">
                      <span className="card-type-badge">USD</span>
                      <span className="card-type-badge">Création : 4 USD</span>
                      <span className="card-type-badge">Frais de transaction : 0 USD</span>
                      <span className="card-type-badge">Frais de rechargement : 3%</span>
                </div>
                  </header>
                  <ul className="card-type-features">
                    <li>Premier dépôt : 10 USD</li>
                    <li>Recharge minimum : 1 USD</li>
                    <li className="card-type-restriction"><strong>Restrictions :</strong> Paiement non-USD interdit, ne marche pas sur e-commerce/Starlink</li>
                  </ul>
                  <footer className="card-type-card-footer">
                  <button
                    type="button"
                      className="select-card-btn"
                      disabled
                  >
                    Indisponible
                  </button>
                  </footer>
                </article>
              </div>
            </div>
            <button
              type="button"
              className="dashboard-action-btn card-type-back-btn card-type-back-button"
              onClick={() => setChoosingCardType(false)}
            >
              <FiArrowLeft /> Retour aux cartes
            </button>
          </>
        ) : (
          <>
            <div className="dashboard-header">
            <h2>Cartes</h2>
            </div>
            {/* Search bar and create card button */}
            <div className="cards-search-container">
              <div className="cards-search-input-container">
                <input
                  type="text"
                  placeholder="Rechercher une carte"
                  value={cardSearch}
                  onChange={e => setCardSearch(e.target.value)}
                  className="cards-search-input"
                />
              </div>
              <button type="button" onClick={openCreateCardModal} className="create-card-btn">Créer une carte</button>
            </div>
            {cards === null ? (
              <div className="cards-table-wrapper">
                <table className="cards-table" aria-label="Liste des cartes">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Nom</th>
                      <th>Type</th>
                      <th>Numéro</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="4" className="table-cell-center">
                        Chargement des cartes...
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : cards.length === 0 ? (
              <div className="cards-table-wrapper">
                <table className="cards-table" aria-label="Liste des cartes">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Nom</th>
                      <th>Type</th>
                      <th>Numéro</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="4" className="table-cell-center-large">
                        <p className="table-empty-message">Vous n'avez pas encore de carte.</p>
                        <button type="button" onClick={openCreateCardModal} className="create-card-btn">
                          Créer une carte
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="cards-table-wrapper">
                <table className="cards-table" aria-label="Liste des cartes">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Nom</th>
                      <th>Type</th>
                      <th>Numéro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cards.filter(card => {
                      const search = cardSearch.trim().toLowerCase();
                      if (!search) return true;
                      return (
                        (card.card_name && card.card_name.toLowerCase().includes(search)) ||
                        (card.card_four_digits && card.card_four_digits.includes(search))
                      );
                    }).length === 0) ? (
                      <tr>
                        <td colSpan="4" className="table-cell-center">
                          Aucune carte trouvée.
                        </td>
                      </tr>
                    ) : (
                      cards.filter(card => {
                        const search = cardSearch.trim().toLowerCase();
                        if (!search) return true;
                        return (
                          (card.card_name && card.card_name.toLowerCase().includes(search)) ||
                          (card.card_four_digits && card.card_four_digits.includes(search))
                        );
                      }).map((card) => (
                        <tr key={card.id} onClick={() => handleCardClick(card.id)} className="card-row-hover" tabIndex={0} aria-label={`Voir la carte ${card.card_name}`}
                          onMouseOver={e => e.currentTarget.classList.add('row-hover')}
                          onFocus={e => e.currentTarget.classList.add('row-hover')}
                          onMouseOut={e => e.currentTarget.classList.remove('row-hover')}
                          onBlur={e => e.currentTarget.classList.remove('row-hover')}
                        >
                          <td>{(() => { const d = new Date(card.issue_date); return (<><span>{d.toLocaleDateString()}</span><br/><span className="card-time-small">{d.toLocaleTimeString()}</span></>); })()}</td>
                          <td>{(() => {
                            if (!card.card_name) return null;
                            if (typeof window !== 'undefined' && window.innerWidth <= 320) {
                              const chunks = card.card_name.match(/.{1,12}/g) || [];
                              return chunks.map((chunk, i) => (
                                <span key={i}>{chunk}{i < chunks.length - 1 ? <br /> : null}</span>
                              ));
                            }
                            return card.card_name;
                          })()}</td>
                          <td className="card-type-cell">
                            {card.service_id === '1' ? (
                              <img src="https://upload.wikimedia.org/wikipedia/commons/5/5c/Visa_Inc._logo_%282021%E2%80%93present%29.svg" alt="Visa" className="card-type-visa-logo" />
                            ) : card.service_id === '2' || card.service_id === '3' ? (
                              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Mastercard_2019_logo.svg/langfr-1280px-Mastercard_2019_logo.svg.png" alt="Mastercard" className="card-type-mastercard-logo" />
                            ) : (
                              <span className="card-type-fallback">{card.card_type}</span>
                            )}
                          </td>
                          <td>
                            {card.creation_status === 'PENDING' ? (
                              <span className="waiting-spinner" title="En cours de création"></span>
                            ) : (
                              <span className="card-digits">{`*${card.card_four_digits}`}</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default CardsPage; 